import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCivilAppellateWizardContext } from '../../../hooks/useCaseRequestWizard';
import { CONTACT_NUMBER_LENGTH, contactNumberError } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { CivilAppellateWizardActions } from './CivilAppellateWizardActions';
import { civilAppellateWizardPath, CivilAppellateWizardShell, FieldError, inpClass } from './CivilAppellateWizardShell';
import { validateCivilAppellateStep } from './civilAppellateStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_NAME = 255;
const MAX_DESC = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

export default function CivilAppellateStep2PartyDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCivilAppellateWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCivilAppellateStep(2, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <CivilAppellateWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionPartyDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.plaintiffApplicantName} <span className="req">*</span>
            </label>
            <input
              id="capl-plaintiff"
              className={inpClass(Boolean(errors.plaintiffName))}
              type="text"
              value={draft.plaintiffName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, plaintiffName: maxLen('plaintiffName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterPlaintiffApplicantName}
            />
            <FieldError message={errors.plaintiffName} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.defendantRespondentName} <span className="req">*</span>
            </label>
            <input
              id="capl-defendant"
              className={inpClass(Boolean(errors.defendantName))}
              type="text"
              value={draft.defendantName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, defendantName: maxLen('defendantName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterDefendantRespondentName}
            />
            <FieldError message={errors.defendantName} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }} id="capl-institutionType">
          <label>
            {f.selectOneOfFollowing} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
            <label className="ccr-radio">
              <input
                type="radio"
                name="institutionType"
                checked={!draft.isIncorporatedBody}
                onChange={() => {
                  setDraft((d) => ({ ...d, isIncorporatedBody: false }));
                  clearErr('isIncorporatedBody');
                }}
              />
              <span>{f.governmentMinistryDepartmentAlt}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="institutionType"
                checked={draft.isIncorporatedBody}
                onChange={() => {
                  setDraft((d) => ({ ...d, isIncorporatedBody: true }));
                  clearErr('isIncorporatedBody');
                }}
              />
              <span>{f.publicCorporationStatutoryAlt}</span>
            </label>
          </div>
          <FieldError message={errors.isIncorporatedBody} />
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.otherInvolvedInstitutions}</label>
          <textarea
            className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
            rows={3}
            value={draft.otherInvolvedInstitutions}
            onChange={(e) =>
              setDraft((d) => ({ ...d, otherInvolvedInstitutions: maxLen('otherInvolvedInstitutions', e.target.value, MAX_DESC) }))
            }
            placeholder={f.otherRelatedInstitutionsHint}
          />
          <FieldError message={errors.otherInvolvedInstitutions} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <div className="ccr-fg">
            <label>
              {f.legalOfficerNameAlt} <span className="req">*</span>
            </label>
            <input
              id="capl-legalOfficer"
              className={inpClass(Boolean(errors.legalOfficerName))}
              type="text"
              value={draft.legalOfficerName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, legalOfficerName: maxLen('legalOfficerName', e.target.value, MAX_NAME) }));
              }}
              placeholder={f.enterLegalOfficerNameInCharge}
            />
            <FieldError message={errors.legalOfficerName} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.contactNumber} <span className="req">*</span>
            </label>
            <input
              id="capl-legalContact"
              className={inpClass(Boolean(errors.legalOfficerContact))}
              type="text"
              inputMode="numeric"
              value={draft.legalOfficerContact}
              onChange={(e) => {
                setDraft((d) => ({
                  ...d,
                  legalOfficerContact: e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT) }));
                clearErr('legalOfficerContact');
              }}
              onBlur={() => {
                const err = contactNumberError(draft.legalOfficerContact);
                if (err) setErrors((e) => ({ ...e, legalOfficerContact: err }));
              }}
              placeholder={f.contactNumberExample}
            />
            <FieldError message={errors.legalOfficerContact} />
          </div>
        </div>
      </SectionCard>

      <CivilAppellateWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        onBack={() => nav(civilAppellateWizardPath(1, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(civilAppellateWizardPath(3, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </CivilAppellateWizardShell>
  );
}
